import { useState, useMemo, useCallback } from 'react';
import {
  Search, Plus, Grid, List, AlertTriangle
} from 'lucide-react';
import { fetchClient } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { usePrecedentsQuery } from '@/hooks/usePrecedentsQuery';
import LibraryDataGrid from './LibraryDataGrid';
import PrecedentCreationView from './PrecedentCreationView';
import type { LibraryItem, LibrarySection } from './types';

export default function ComprehensiveLibraryView() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCreationView, setShowCreationView] = useState(false);
  const [editingItem, setEditingItem] = useState<LibraryItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: rawPrecedents = [], isFetching: loading } =
    usePrecedentsQuery(searchQuery || undefined);

  const items: LibraryItem[] = useMemo(() =>
    rawPrecedents.map((p) => ({
      id: String(p.id),
      type: 'precedents' as LibrarySection,
      title: p.title || 'Untitled Precedent',
      description: p.description || '',
      fileName: p.file_name || undefined,
      metadata: {},
      usageCount: p.reuse_count || 0,
      createdAt: p.created_at || '',
      updatedAt: p.updated_at || p.created_at || '',
      visibility: 'team' as const,
      analyzed: p.heading_hierarchy != null,
    })),
    [rawPrecedents],
  );

  const invalidatePrecedents = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/precedents"] });
  }, [queryClient]);

  // Both click and edit open the creation/edit view
  const handleOpenEdit = useCallback((item: LibraryItem) => {
    setEditingItem(item);
    setShowCreationView(true);
  }, []);

  const handleDeleteRequest = useCallback((item: LibraryItem) => {
    setDeleteError(null);
    setDeleteTarget(item);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const errMsg = "Failed to delete precedent. Please try again.";
    try {
      const { error } = await fetchClient.DELETE("/api/v1/precedents/{precedent_id}", {
        params: { path: { precedent_id: deleteTarget.id } },
      });
      if (error) {
        setDeleteError(errMsg);
        return;
      }
      setDeleteTarget(null);
      invalidatePrecedents();
    } catch {
      setDeleteError(errMsg);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, invalidatePrecedents]);

  // Creation/Edit full-screen view
  if (showCreationView) {
    return (
      <PrecedentCreationView
        existingItem={editingItem}
        onBack={() => {
          setShowCreationView(false);
          setEditingItem(null);
        }}
        onSave={async () => {
          setShowCreationView(false);
          setEditingItem(null);
          invalidatePrecedents();
        }}
      />
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="w-full px-6 py-4 flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Precedents
            </h1>
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ backgroundColor: 'var(--surface)', color: 'var(--text-secondary)' }}
            >
              {items.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="p-2 border rounded"
              style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-alt)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
            </button>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowCreationView(true);
              }}
              className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-[var(--accent-hover)] text-sm font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Precedent
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-3 flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
            <input
              type="text"
              placeholder="Search library..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
            />
          </div>
        </div>

        {/* Data Grid */}
        <div className="flex-1 overflow-auto">
          <LibraryDataGrid
            items={items}
            loading={loading}
            viewMode={viewMode}
            onItemSelect={handleOpenEdit}
            onItemEdit={handleOpenEdit}
            onItemDelete={handleDeleteRequest}
          />
        </div>

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6" style={{ backgroundColor: 'var(--background)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'color-mix(in srgb, var(--error) 10%, transparent)' }}>
                <AlertTriangle className="w-5 h-5" style={{ color: 'var(--error)' }} />
              </div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Delete precedent?</h3>
            </div>
            <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
              &ldquo;{deleteTarget.title}&rdquo; will be permanently deleted.
            </p>
            {deleteError && (
              <p className="text-sm mt-2" style={{ color: 'var(--error)' }}>{deleteError}</p>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors"
                style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-alt)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
